import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  Billing,
  Payment,
  PaymentOptions,
} from '../../shared/core/state/order/order.model';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';

import * as LiveClassActions from '../../shared/core/state/live-class/live-class.action';
import { Invoice, InvoiceStatus, TenantBankAccount } from '../../shared/models/invoice.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@black/ui';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { selectUser } from '../../shared/core/state/users/user.selectors';
import { environment } from '../../../../src/environments/environment';

@Component({
  selector: 'black-invoice-payment',
  templateUrl: './invoice-payment.component.html',
  styleUrl: './invoice-payment.component.css',
  standalone: false,
})
export class InvoicePaymentComponent implements OnInit {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private liveClassService = inject(LiveClassService);
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);

  currentInvoice: Invoice | null = null;
  tenantBankAccounts: TenantBankAccount[] = [];
  selectedPaymentMethod: 'bank' | 'payhere' | null = null;
  isPayHereEnabled = false;
  isBankTransferEnabled = false;
  isCustomGateway = false;
  payhereError: string | null = null;
  isLoading = true;
  private isConfigLoaded = false;
  private isInvoiceLoaded = false;
  readonly PaymentOptions = PaymentOptions;

  billingForm = this.fb.group({
    name: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    city: ['', Validators.required],
    province: ['', Validators.required],
    postal: ['', Validators.required],
    contact: ['', Validators.required],
    country: ['', Validators.required],
  });

  constructor() {
    this.store
      .select((state) => state.liveClass.subscription)
      .pipe(takeUntilDestroyed())
      .subscribe((subscription) => {
        const invoice = subscription?.invoice;
        if (invoice) {
          this.currentInvoice = invoice;
          this.isInvoiceLoaded = true;
          this.checkLoaded();

          if (!this.isInvoicePayable(this.currentInvoice.status)) {
            this.router.navigate(['dashboard', 'user']);
            this.toastService.showToast(
              'Invoice is not payable, redirecting back to dashboard',
              'warning'
            );
          }
        } else if (subscription?.error) {
          this.isInvoiceLoaded = true;
          this.checkLoaded();
        }
      });
  }

  private checkLoaded(): void {
    if (this.isConfigLoaded && this.isInvoiceLoaded) {
      this.isLoading = false;
    }
  }

  ngOnInit(): void {
    const invoiceId = this.route.snapshot.paramMap.get('id');
    if (invoiceId) {
      this.store.dispatch(LiveClassActions.fetchInvoice({ invoiceId }));
      this.liveClassService
        .getInvoicePaymentConfig(invoiceId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.isConfigLoaded = true;
            this.checkLoaded();
          })
        )
        .subscribe({
          next: (config) => {
            this.isCustomGateway = config?.isSystemManaged === false;
            const rawBanks = config?.bankAccounts || [];
            this.tenantBankAccounts = rawBanks.filter(
              (b) => b.bankName?.trim() && b.accountNumber?.trim()
            );
            this.isPayHereEnabled = Boolean(
              config?.acceptedMethods?.includes('PAYHERE') && (!this.isCustomGateway || config?.payhereMerchantId)
            );
            this.isBankTransferEnabled = Boolean(
              (config?.acceptedMethods?.includes('BANK_TRANSFER') ?? true) &&
                this.tenantBankAccounts.length > 0
            );

            if (!this.isPayHereEnabled && this.selectedPaymentMethod === 'payhere') {
              this.selectedPaymentMethod = this.isBankTransferEnabled ? 'bank' : null;
            } else if (!this.isBankTransferEnabled && this.selectedPaymentMethod === 'bank') {
              this.selectedPaymentMethod = this.isPayHereEnabled ? 'payhere' : null;
            } else if (!this.selectedPaymentMethod) {
              this.selectedPaymentMethod = this.isPayHereEnabled
                ? 'payhere'
                : (this.isBankTransferEnabled ? 'bank' : null);
            }
          },
          error: (err) => {
            console.error('Failed to load invoice payment config', err);
            this.tenantBankAccounts = [];
            this.isPayHereEnabled = false;
            this.isBankTransferEnabled = false;
            this.selectedPaymentMethod = null;
          },
        });
    } else {
      this.isLoading = false;
    }

    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (!user) return;

        const fullName =
          [user.name?.firstName, user.name?.lastName]
            .filter(Boolean)
            .join(' ') || '';

        this.billingForm.patchValue({
          name: fullName || this.billingForm.controls.name.value,
          addressLine1:
            user.profile?.address?.addressLine1 ||
            this.billingForm.controls.addressLine1.value,
          addressLine2:
            user.profile?.address?.addressLine2 ||
            this.billingForm.controls.addressLine2.value,
          city:
            user.profile?.address?.city || this.billingForm.controls.city.value,
          province:
            user.profile?.address?.province ||
            this.billingForm.controls.province.value,
          postal:
            user.profile?.address?.postalCode ||
            this.billingForm.controls.postal.value,
          contact: user.mobile || this.billingForm.controls.contact.value,
          country:
            user.profile?.address?.country ||
            this.billingForm.controls.country.value,
        });
      });
  }

  onPaymentSuccess(payment: Payment) {
    if (this.currentInvoice?._id) {
      this.store.dispatch(
        LiveClassActions.processPayment({
          invoiceId: this.currentInvoice?._id,
          payment: payment,
        })
      );
    }

    this.store
      .select((state) => state.liveClass.subscription)
      .subscribe((subscription) => {
        if (!subscription?.processingPayment && subscription?.paymentSuccess) {
          this.router.navigate(['subscription', 'payment-success']);
        }
      });
  }

  processPayherePayment() {
    this.billingForm.markAllAsTouched();
    if (!this.billingForm.valid) {
      return;
    }

    const billingData = this.billingForm.value;
    const billing: Billing = {
      name: billingData.name ?? '',
      addressLine1: billingData.addressLine1 ?? '',
      addressLine2: billingData.addressLine2 ?? '',
      city: billingData.city ?? '',
      province: billingData.province ?? '',
      postalCode: billingData.postal ?? '',
      contactNumber: billingData.contact ?? '',
      country: billingData.country ?? '',
    };

    const returnUrl = `${window.location.origin}/subscription/payment-success`;
    const cancelUrl = window.location.href;

    this.liveClassService
      .initiateInvoicePayherePayment(
        this.currentInvoice?._id ?? '',
        billing,
        returnUrl,
        cancelUrl
      )
      .subscribe({
        next: (checkoutData) => {
          this.payhereError = null;
          this.submitPayhereForm(checkoutData);
        },
        error: (err) => {
          console.error('PayHere initiation failed:', err);
          this.payhereError =
            'Failed to initialize payment. Please try again or contact support.';
        },
      });
  }

  private submitPayhereForm(checkoutData: Record<string, string>) {
    const form = document.createElement('form');
    form.method = 'POST';
    const isSandbox =
      checkoutData['sandbox'] === 'true' ||
      (!environment.production && checkoutData['sandbox'] !== 'false');
    form.action = `https://${
      isSandbox ? 'sandbox' : 'www'
    }.payhere.lk/pay/checkout`;

    Object.keys(checkoutData).forEach((key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = checkoutData[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  calculatePayable(total: number, payments: Payment[]) {
    const totalPaid = payments
      .filter((payment) => payment.isApproved)
      .reduce((total, payment) => (payment.amount ?? 0) + total, 0);

    return total - totalPaid;
  }

  isInvoicePayable(invoiceStatus: InvoiceStatus) {
    return ![
      InvoiceStatus.CANCELLED,
      InvoiceStatus.PAID,
      InvoiceStatus.REFUNDED,
      InvoiceStatus.REFUND_REQUESTED,
    ].includes(invoiceStatus);
  }
}
